import React, { useState, useMemo } from 'react';
import type { Workspace, Bookmark, BookmarkContentItem, SectionComponent } from '@ext-bookmark/shared/types';
import { normalizeUrl } from '@ext-bookmark/shared/utils';

interface SearchBarProps {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  searchMode: 'workspace' | 'all';
  onSearchModeChange: (mode: 'workspace' | 'all') => void;
  onBookmarkClick: (bookmark: Bookmark, workspaceId: string, sectionId: string) => void;
}

interface SearchResult {
  bookmark: Bookmark;
  workspaceId: string;
  workspaceName: string;
  sectionId: string;
  sectionName: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  workspaces,
  currentWorkspace,
  searchMode,
  onSearchModeChange,
  onBookmarkClick,
}) => {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const searchResults = useMemo<SearchResult[]>(() => {
    if (!query.trim()) return [];

    const lowerQuery = query.toLowerCase();
    const results: SearchResult[] = [];

    const safeWorkspaces = workspaces || [];
    const searchWorkspaces = searchMode === 'all' ? safeWorkspaces : (currentWorkspace ? [currentWorkspace] : []);

    searchWorkspaces.forEach((workspace) => {
      const components = workspace.components || [];
      
      const sectionComponents = components.filter(
        (c): c is SectionComponent => c.componentType === 'section' && !c.parentId
      );
      
      sectionComponents.forEach((section) => {
        const contentItems = section.contentItems || [];
        contentItems.forEach((item) => {
          if (item.type === 'bookmark') {
            const bookmarkItem = item as BookmarkContentItem;
            const bookmark = bookmarkItem.bookmark;
            
            const matchesName = bookmark.name.toLowerCase().includes(lowerQuery);
            const matchesUrl = bookmark.url.toLowerCase().includes(lowerQuery);
            const matchesNote = bookmark.note?.toLowerCase().includes(lowerQuery);

            if (matchesName || matchesUrl || matchesNote) {
              results.push({
                bookmark,
                workspaceId: workspace.id,
                workspaceName: workspace.name,
                sectionId: section.id,
                sectionName: section.name,
              });
            }
          }
        });
      });
    });

    return results;
  }, [query, workspaces, currentWorkspace, searchMode]);

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 dark:bg-yellow-800">{part}</span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            onBlur={() => {
              setTimeout(() => setShowResults(false), 200);
            }}
            className="w-full px-4 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="搜索书签..."
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => onSearchModeChange('workspace')}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              searchMode === 'workspace'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            工作区
          </button>
          <button
            onClick={() => onSearchModeChange('all')}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              searchMode === 'all'
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            全部
          </button>
        </div>
      </div>

      {showResults && searchResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          {searchResults.map((result, index) => (
            <div
              key={`${result.workspaceId}-${result.sectionId}-${result.bookmark.id}-${index}`}
              onClick={() => {
                onBookmarkClick(result.bookmark, result.workspaceId, result.sectionId);
                setShowResults(false);
                setQuery('');
              }}
              className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {highlightText(result.bookmark.name, query)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                    {highlightText(normalizeUrl(result.bookmark.url), query)}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {result.workspaceName} / {result.sectionName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && query && searchResults.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400 z-50">
          未找到匹配的书签
        </div>
      )}
    </div>
  );
};

export default SearchBar;
